import { useState, useCallback, useEffect } from 'react';
import scraperApi from 'api';

export type TLDs = {
  amazon: string[];
  google: string[];
  ebay: string[];
  redfin: string[];
  walmart: string[];
};

export type TLDsState = {
  tlds: TLDs | null;
  isLoading: boolean;
  error: Error | null;
};

const defaultTLDs: TLDs = {
  amazon: [],
  google: [],
  ebay: [],
  redfin: [],
  walmart: []
};

export const useTLDs = (domain: keyof TLDs) => {
  const [state, setState] = useState<TLDsState>({
    tlds: null,
    isLoading: false,
    error: null
  });

  const hasBeenFetched = state.tlds !== null;

  const fetchTLDs = useCallback(async () => {
    if (state.isLoading || hasBeenFetched) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await scraperApi.settings.availableTLDs();
      setState({
        tlds: response,
        isLoading: false,
        error: null
      });
      return response;
    } catch (error) {
      const err = error as Error;
      setState({
        tlds: defaultTLDs,
        isLoading: false,
        error: err
      });
      // Don't throw the error, just return default TLDs
      return defaultTLDs;
    }
  }, [state.isLoading, hasBeenFetched]);

  useEffect(() => {
    fetchTLDs();
  }, [fetchTLDs]);

  const getTLDs = useCallback((type: keyof TLDs) => {
      return state.tlds?.[type] || defaultTLDs[type];
    }, [state.tlds]);

  // Ensure it's always an array, even if the domain doesn't exist
  const domainTLDs = state.tlds?.[domain] || defaultTLDs[domain] || [];

  return {
    ...state,
    tlds: domainTLDs,
    fetchTLDs,
    getTLDs
  };
};